// Service branches and military ranks moved to @/lib/military-constants.ts

export const FUNDING_STATUS_OPTIONS = [
  'Unfunded',
  'Seeking Funding',
  'Partially Funded',
  'Fully Funded',
] as const;

// ===========================
// Registration Form Constants
// ===========================

import type { OrganizationType, ProjectCategory, ProjectData } from '@/types';

export type ParticipantType = 'student' | 'faculty' | 'industry' | 'alumni' | 'guest' | 'nps_staff' | 'active_reserve_military' | 'government_employee' | 'other';

export type ProjectEntry = {
  category: ProjectCategory;
  title: string;
  description: string;
  department: string;
  stage: string;
  classification: string;
  researchAreas: string[];
  keywords: string[];
  seeking: string[];
  students: string[];
  dodImpact: string;
  technologyFocus: string[];
  fundingStatus: string;
  sponsorOrganization: string;
  requirements: string;
  benefits: string;
  location: string;
  duration: string;
  deadline: string;
  dodAlignment: string[];
  pocFirstName: string;
  pocLastName: string;
  pocEmail: string;
  pocRank: string;
};

export const makeEmptyProject = (category: ProjectCategory = 'research'): ProjectEntry => ({
  category,
  title: '',
  description: '',
  department: '',
  stage: '',
  classification: 'Unclassified',
  researchAreas: [],
  keywords: [],
  seeking: [],
  students: [],
  dodImpact: '',
  technologyFocus: [],
  fundingStatus: '',
  sponsorOrganization: '',
  requirements: '',
  benefits: '',
  location: '',
  duration: '',
  deadline: '',
  dodAlignment: [],
  pocFirstName: '',
  pocLastName: '',
  pocEmail: '',
  pocRank: '',
});

export const projectDataToEntry = (pd: ProjectData): ProjectEntry => ({
  category: pd.category || 'research',
  title: pd.title || '',
  description: pd.description || '',
  department: pd.department || '',
  stage: pd.stage || '',
  classification: pd.classification || 'Unclassified',
  researchAreas: pd.researchAreas || [],
  keywords: pd.keywords || [],
  seeking: pd.seeking || [],
  students: pd.students || [],
  dodImpact: pd.dodImpact || '',
  technologyFocus: pd.technologyFocus || [],
  fundingStatus: pd.fundingStatus || '',
  sponsorOrganization: pd.sponsorOrganization || '',
  requirements: pd.requirements || '',
  benefits: pd.benefits || '',
  location: pd.location || '',
  duration: pd.duration || '',
  deadline: pd.deadline || '',
  dodAlignment: pd.dodAlignment || [],
  pocFirstName: pd.pocFirstName || '',
  pocLastName: pd.pocLastName || '',
  pocEmail: pd.pocEmail || '',
  pocRank: pd.pocRank || '',
});

export const ORG_LABELS: Record<OrganizationType, string> = {
  nps: 'Naval Postgraduate School',
  government: 'Government / Military',
  industry: 'Industry',
};

export const PARTICIPANT_TYPE_LABELS: Record<ParticipantType, string> = {
  student: 'NPS Student',
  nps_staff: 'NPS Faculty/Staff',
  alumni: 'NPS Alumni',
  industry: 'Industry',
  active_reserve_military: 'Active/Reserve Military',
  government_employee: 'Government Employee',
  other: 'Other',
  faculty: 'Faculty',
  guest: 'Guest',
};

export const SESSION_TYPE_LABELS: Record<string, string> = {
  Featured: 'Featured',
  Networking: 'Networking',
  Panel: 'Panel',
  Breakout: 'Breakout',
  Workshop: 'Workshop',
};

export const CATEGORY_LABELS: Record<ProjectCategory, string> = {
  research: 'Research Project',
  funding: 'Funding Opportunity',
  internship: 'Internship',
  competition: 'Competition',
  collaboration: 'Collaboration',
};

export const DEFAULT_CATEGORY: Record<OrganizationType, ProjectCategory> = {
  nps: 'research',
  industry: 'funding',
  government: 'collaboration',
};

export const DOD_ALIGNMENT_SUGGESTIONS = [
  'National Defense Strategy',
  'Joint Warfighting Concept',
  'Cyber Operations',
  'Space Operations',
  'AI/ML Strategy',
  'Supply Chain Security',
  'Maritime Security',
  'Information Warfare',
];

// ===========================
// V2 Registration Form Constants
// ===========================

export type RegistrationType = 'exhibitor' | 'nps_project' | 'attendee';

/** Technology areas for Converge 2026 — 19 items using AccelerationInterest enum values */
export const TECHNOLOGY_AREAS = [
  { value: 'additive_manufacturing', label: 'Additive Manufacturing (AM)' },
  { value: 'advanced_materials', label: 'Advanced Materials' },
  { value: 'ai_ml', label: 'Artificial Intelligence & Machine Learning (AI/ML)' },
  { value: 'ar_vr_xr', label: 'Augmented Reality & Virtual Reality (AR/VR)' },
  { value: 'autonomous_systems', label: 'Autonomous Systems' },
  { value: 'communications', label: 'Communications' },
  { value: 'contested_logistics', label: 'Contested Logistics' },
  { value: 'cybersecurity', label: 'Cybersecurity' },
  { value: 'data_analytics', label: 'Data Analysis/Data Analytics' },
  { value: 'directed_energy', label: 'Directed Energy' },
  { value: 'electronic_warfare', label: 'Electronic Warfare (EW)' },
  { value: 'future_g', label: 'Future Generation Wireless Technology (FutureG)' },
  { value: 'hypersonics', label: 'Hypersonics' },
  { value: 'integrated_networks', label: 'Integrated Networks' },
  { value: 'modeling_simulation', label: 'Modeling and Simulation (MODSIM)' },
  { value: 'quantum', label: 'Quantum' },
  { value: 'robotics', label: 'Robotics' },
  { value: 'space', label: 'Space' },
  { value: 'undersea_warfare', label: 'Undersea Warfare (USW)' },
] as const;

export type TechnologyAreaValue = (typeof TECHNOLOGY_AREAS)[number]['value'];

export const TECHNOLOGY_AREA_LABEL_MAP: Record<string, string> = {
  ...Object.fromEntries(
    TECHNOLOGY_AREAS.map(({ value, label }) => [value, label])
  ),
  // Legacy values — removed from enum but may exist in String[] columns on older records
  blockchain: 'Blockchain',
  iot: 'IoT',
  cloud_computing: 'Cloud Computing',
  edge_computing: 'Edge Computing',
  biotechnology: 'Biotechnology',
  clean_energy: 'Clean Energy',
  human_machine_interface: 'Human-Machine Interface',
  digital_twins: 'Digital Twins',
  nanotechnology: 'Nanotechnology',
  // Legacy renamed values — may exist in String[] columns before migration
  quantum_computing: 'Quantum',
  five_six_g_networks: 'Future Generation Wireless Technology (FutureG)',
  space_technology: 'Space',
};

export const SEEKING_COLLABORATION_OPTIONS = [
  { value: 'data_access_sharing', label: 'Data Access/Sharing' },
  { value: 'education_partnership', label: 'Education Partnership' },
  { value: 'funding', label: 'Funding' },
  { value: 'pilot_program', label: 'Pilot Program' },
  { value: 'prototyping_experimentation', label: 'Prototyping & Experimentation' },
  { value: 'rd_partnership', label: 'R&D Partnership' },
  { value: 'students_interns', label: 'Students/Interns' },
  { value: 'tech_licensing', label: 'Tech Licensing' },
  { value: 'tech_transfer', label: 'Tech Transfer' },
] as const;

export const SEEKING_LABEL_MAP: Record<string, string> = Object.fromEntries(
  SEEKING_COLLABORATION_OPTIONS.map(({ value, label }) => [value, label])
);

/**
 * Legacy display labels (and minor variants) → current enum values for the
 * Project.seeking[] / Partner.seeking[] arrays. Records written before the
 * Converge 2026 taxonomy migration stored display strings; the public and
 * admin filter both normalize through this map before comparing against
 * SEEKING_COLLABORATION_OPTIONS values.
 *
 * Mirrors backend/src/utils/seekingNormalize.ts — keep entries in sync.
 */
export const SEEKING_LABEL_TO_ENUM: Record<string, string> = {
  // Current display labels
  'Data Access/Sharing': 'data_access_sharing',
  'Education Partnership': 'education_partnership',
  'Funding Opportunities': 'funding',
  'Pilot Programs': 'pilot_program',
  'Prototyping & Experimentation': 'prototyping_experimentation',
  'Research Partnership': 'rd_partnership',
  'Student Interns': 'students_interns',
  'Students/Interns': 'students_interns',
  'Tech Licensing': 'tech_licensing',
  'Tech Transfer': 'tech_transfer',

  // Issue #906 — values seen in DB but missing from filter
  'Research collaboration': 'rd_partnership',
  'Research Collaboration': 'rd_partnership',
  'NPS research partnership': 'rd_partnership',
  'NPS Research Partnership': 'rd_partnership',
  'Pilot projects': 'pilot_program',
  'Pilot Projects': 'pilot_program',
  'CRADA': 'rd_partnership',

  // Legacy SEEKING_OPTIONS values (retired but still present in old records)
  'Funding': 'funding',
  'Partners': 'rd_partnership',
  'Students': 'students_interns',
  'Mentorship': 'students_interns',
  'Technical Expertise': 'rd_partnership',
  'Industry Collaboration': 'rd_partnership',
  'Industry Partnership': 'rd_partnership',
  'Government Sponsor': 'funding',
  'Test & Evaluation': 'prototyping_experimentation',
  'Pilot Opportunity': 'pilot_program',
  'Data Access': 'data_access_sharing',

  // Snake_case variants observed in dev/prod data and AI-generated payloads
  'research_collaboration': 'rd_partnership',
  'nps_research_partnership': 'rd_partnership',
  'pilot_projects': 'pilot_program',
  'industry_partners': 'rd_partnership',
  'industry_partnership': 'rd_partnership',
  'industry_collaboration': 'rd_partnership',
  'government_sponsor': 'funding',
  'test_evaluation': 'prototyping_experimentation',
  'test_and_evaluation': 'prototyping_experimentation',
  'technical_expertise': 'rd_partnership',
  'pilot_opportunity': 'pilot_program',
  'data_access': 'data_access_sharing',
  'crada': 'rd_partnership',
};

export const EXHIBITOR_ORG_TYPES = [
  { value: 'Industry', label: 'Industry' },
  { value: 'Military Command', label: 'Military Command' },
  { value: 'Dept. of War Organization', label: 'Dept. of War Organization' },
  { value: 'Other Government Organization', label: 'Other Government Organization' },
] as const;

export const ATTENDEE_PARTICIPANT_TYPES = [
  { value: 'student', label: 'NPS Student' },
  { value: 'nps_staff', label: 'NPS Faculty/Staff' },
  { value: 'alumni', label: 'NPS Alumni' },
  { value: 'industry', label: 'Industry' },
  { value: 'active_reserve_military', label: 'Active/Reserve Military' },
  { value: 'government_employee', label: 'Government Employee' },
  { value: 'other', label: 'Other' },
] as const;

export const PARTNER_ORG_TYPES = [
  { value: 'industry', label: 'Industry' },
  { value: 'government', label: 'Government / Military' },
  { value: 'nps', label: 'NPS' },
] as const;

/**
 * Frontend mirror of backend `mapExhibitorOrgTypeToPartnerCategory`.
 * Keep these two implementations in sync — they're the contract between
 * the registration/admin write paths and the Opportunities/Partner filters.
 */
export function getPartnerOrgCategory(orgType: string | null | undefined): string | null {
  if (!orgType) return null;
  const t = orgType.toLowerCase().trim();
  if (t === 'industry' || t === 'government' || t === 'nps') return t;
  if (t.includes('nps') || t.includes('naval postgraduate') || t.includes('faculty')) return 'nps';
  // Industry keywords beat military/gov keywords so "Defense Industry Inc." stays Industry.
  if (t.includes('industry') || t.includes('corporate') || t.includes('company') || t === 'private') return 'industry';
  if (
    t.includes('military') || t.includes('government') || t.includes('gov') ||
    t.includes('dept') || t.includes('department') || t.includes('dod') ||
    t.includes('defense') || t.includes('defence') || t.includes('federal') ||
    t.includes('agency') || t.includes('bureau') || t.includes('fleet') ||
    t.includes('command') || t.includes('corps') || t.includes('force')
  ) return 'government';
  return null;
}

export const SECURITY_CLASSIFICATIONS = [
  { value: 'unclassified', label: 'Unclassified' },
  { value: 'cui', label: 'CUI' },
  { value: 'cui_specified', label: 'CUI Specified' },
  { value: 'confidential', label: 'Confidential' },
  { value: 'secret', label: 'Secret' },
  { value: 'ts_sci', label: 'TS/SCI' },
  { value: 'unknown', label: 'Unknown' },
] as const;

export const DEVELOPMENT_STAGES = [
  { value: 'concept', label: 'Concept' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'pilot_ready', label: 'Pilot Ready' },
  { value: 'deployed', label: 'Deployed' },
] as const;

export const FUNDING_STATUS_V2 = [
  { value: 'funded', label: 'Funded' },
  { value: 'partially_funded', label: 'Partially Funded' },
  { value: 'seeking_funding', label: 'Seeking Funding' },
] as const;

export const EXHIBITOR_ACKNOWLEDGEMENTS = [
  'I understand that space is limited and selected organizations will be notified by June 22. My organization\'s spot is not confirmed until the exhibitor fee is paid by July 6. The fee covers event costs and supports up to 4 attendees; additional attendees will incur additional fees.',
  'I understand that no refunds will be given for cancellations after July 10, and that I must provide contact information and campus access forms for all attendees from my organization by July 8.',
  'I acknowledge that by registering, I will be added to the NPS Foundation mailing list and can opt out at any time. I also acknowledge that I will receive communications from converge@npsfoundation.org and athena@npsfoundation.org regarding the event and about Athena, a defense collaborative research platform.',
] as const;

export const NPS_PROJECT_ACKNOWLEDGEMENTS = [
  'I understand that my project information will be included in the Converge event app, and that the NPS Foundation will use my submission to create posters or other materials for the event and other communication channels. Final poster approval lies with me.',
  'I understand that if provided with a table and TV screen during the showcase, I must be present for the entire showcase on July 23, from 1300 to 1600, in the Herrmann Hall Ballroom.',
  'I acknowledge that by registering, I will be added to the NPS Foundation mailing list and can opt out at any time. I also acknowledge that I will receive communications from converge@npsfoundation.org and athena@npsfoundation.org regarding the event and about Athena, a defense collaborative research platform.',
] as const;

export const ATTENDEE_ACKNOWLEDGEMENTS = [
  'I acknowledge that I am responsible for my own access to the NPS campus using a DODID, DBIDS or other approved campus access pass (REAL ID or official passport required). The NPS Foundation is extremely limited in providing escort onto campus and may not be available to support.',
  'I acknowledge that by registering, I will be added to the NPS Foundation mailing list and can opt out at any time. I also acknowledge that I will receive communications from converge@npsfoundation.org and athena@npsfoundation.org regarding the event and about Athena, a defense collaborative research platform.',
] as const;

/** Walk-in acknowledgements — campus access ack omitted since walk-ins are already on-site */
export const WALKIN_ACKNOWLEDGEMENTS = [
  'I acknowledge that by registering, I will be added to the NPS Foundation mailing list and can opt out at any time. I also acknowledge that I will receive communications from converge@npsfoundation.org and athena@npsfoundation.org regarding the event and about Athena, a defense collaborative research platform.',
] as const;

/** V2 project entry for exhibitor collaborative projects */
export type ExhibitorProjectEntry = {
  title: string;
  description: string;
  seekingCollaboration: string[];
  isCrada: boolean;
  classification: string;
  stage: string;
  fundingStatus: string;
  dodImpact: string;
};

export const makeEmptyExhibitorProject = (): ExhibitorProjectEntry => ({
  title: '',
  description: '',
  seekingCollaboration: [],
  isCrada: false,
  classification: '',
  stage: '',
  fundingStatus: '',
  dodImpact: '',
});

/** V2 project entry for NPS student/faculty projects */
export type NpsProjectEntry = {
  title: string;
  description: string;
  seekingCollaboration: string[];
  classification: string;
  stage: string;
  fundingStatus: string;
  collaboratingOrganizations: string;
  technologyAreas: string[];
  dodImpact: string;
};

export const makeEmptyNpsProject = (): NpsProjectEntry => ({
  title: '',
  description: '',
  seekingCollaboration: [],
  classification: '',
  stage: '',
  fundingStatus: '',
  collaboratingOrganizations: '',
  technologyAreas: [],
  dodImpact: '',
});
